import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Columns, Users, CheckCircle2 } from 'lucide-react'
import { PageContainer, PageHeader } from '@/components/common'
import { WorkflowLayout } from '@/components/upload/workflow/WorkflowLayout'
import { DriverFieldMappingStep } from '@/components/upload/driver-workflow/DriverFieldMappingStep'
import { DriverMatchReviewStep } from '@/components/upload/driver-workflow/DriverMatchReviewStep'
import { DriverCompleteStep } from '@/components/upload/driver-workflow/DriverCompleteStep'
import { useDriverUploadWorkflowController } from '@/hooks/useDriverUploadWorkflowController'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { DriverWorkflowStep, FieldConflictResolution, UnmatchedDriverAction } from '@/types'

const DRIVER_STEPS: Array<{ key: DriverWorkflowStep; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { key: 'field-mapping', label: 'Kolumnmappning', icon: Columns },
  { key: 'driver-review', label: 'Granska förare', icon: Users },
  { key: 'complete', label: 'Klart', icon: CheckCircle2 }
]

export function DriverUploadWorkflowPage() {
  const navigate = useNavigate()
  const { uploadId } = useParams<{ uploadId: string }>()
  const controller = useDriverUploadWorkflowController()
  const { state: workflow, setStep, updateState, resetState, initialize, runFieldMapping, processDriverUpdates, canProceedToNextStep } = controller

  useEffect(() => {
    if (!uploadId) return
    resetState()
    initialize(uploadId)
    return () => {
      resetState()
    }
  }, [uploadId, initialize, resetState])

  const handleConflictResolutionChange = (rowId: string, resolution: FieldConflictResolution) => {
    const next = new Map(workflow.conflictResolutions)
    const existing = next.get(rowId) || []
    const filtered = existing.filter(item => item.fieldName !== resolution.fieldName)
    next.set(rowId, [...filtered, resolution])
    updateState({ conflictResolutions: next })
  }

  const handleUnmatchedActionChange = (rowId: string, action: UnmatchedDriverAction) => {
    const next = new Map(workflow.unmatchedActions)
    next.set(rowId, action)
    updateState({ unmatchedActions: next })
  }

  const handleMatchRejection = (rowId: string) => {
    const row = workflow.driverUpdateRows.find(r => r.rowId === rowId)
    if (!row) return
    const next = new Map(workflow.unmatchedActions)
    next.set(rowId, {
      rowId,
      rowIndex: row.rowIndex,
      action: 'create_new',
      newDriverData: {
        primaryName: row.identifiers.name || '',
        email: row.identifiers.email,
        phone: row.identifiers.phone,
        personalCode: row.identifiers.personalCode
      }
    })
    updateState({ unmatchedActions: next })
  }

  const handleMatchRestored = (rowId: string) => {
    const next = new Map(workflow.unmatchedActions)
    next.delete(rowId)
    updateState({ unmatchedActions: next })
  }

  const canGoBack =
    workflow.step === 'driver-review'

  const canGoNext =
    workflow.step !== 'complete' &&
    canProceedToNextStep()

  const handleNext = async () => {
    if (!canProceedToNextStep()) return

    if (workflow.step === 'field-mapping') {
      await runFieldMapping()
      return
    }

    if (workflow.step === 'driver-review') {
      await processDriverUpdates()
      return
    }
  }

  const handleBack = () => {
    if (!canGoBack) return

    if (workflow.step === 'driver-review') {
      setStep('field-mapping')
    }
  }

  if (workflow.loading) {
    return (
      <PageContainer>
        <PageHeader
          title="Förbereder uppladdningen"
          subtitle="Hämtar uppladdad fil och tidigare val."
        />
      </PageContainer>
    )
  }

  if (!workflow.driverUploadStaging || !workflow.originalUpload) {
    return (
      <PageContainer>
        <PageHeader
          title="Uppladdningen hittades inte"
          subtitle="Kontrollera att filen är uppladdad och försök igen."
        />
      </PageContainer>
    )
  }

  const currentStepIndex = workflow.step === 'complete'
    ? DRIVER_STEPS.length - 1
    : DRIVER_STEPS.findIndex(step => step.key === workflow.step)

  const headers = workflow.originalUpload.headers || []

  return (
    <PageContainer>
      <PageHeader
        title={DRIVER_STEPS[currentStepIndex]?.label ?? 'Arbetsflöde'}
        subtitle={getStepDescription(workflow.step)}
      />

      {workflow.error && (
        <Alert className="mb-6" variant="destructive">
          <AlertDescription>{workflow.error}</AlertDescription>
        </Alert>
      )}

      <WorkflowLayout
        fileName={workflow.originalUpload.fileName}
        currentStepIndex={currentStepIndex}
        steps={DRIVER_STEPS}
        canGoBack={canGoBack}
        canGoNext={canGoNext}
        isProcessing={workflow.isProcessing}
        onBack={handleBack}
        onNext={handleNext}
        nextButtonLabel={workflow.step === 'driver-review' ? 'Bearbeta' : 'Nästa'}
      >
        {workflow.step === 'field-mapping' && (
          <DriverFieldMappingStep
            headers={headers}
            identifierMapping={workflow.identifierMapping}
            profileFieldMapping={workflow.profileFieldMapping}
            onIdentifierMappingChange={(mapping) => updateState({ identifierMapping: mapping })}
            onProfileFieldMappingChange={(mapping) => updateState({ profileFieldMapping: mapping })}
          />
        )}

        {workflow.step === 'driver-review' && (
          <DriverMatchReviewStep
            driverUpdateRows={workflow.driverUpdateRows}
            availableDrivers={workflow.availableDrivers}
            conflictResolutions={workflow.conflictResolutions}
            unmatchedActions={workflow.unmatchedActions}
            onConflictResolutionChange={handleConflictResolutionChange}
            onUnmatchedActionChange={handleUnmatchedActionChange}
            onMatchRejected={handleMatchRejection}
            onMatchRestored={handleMatchRestored}
          />
        )}

        {workflow.step === 'complete' && (
          <DriverCompleteStep
            result={workflow.uploadResult}
            onGoToDrivers={() => navigate('/forare')}
            onNewUpload={() => navigate('/forare/upload')}
          />
        )}
      </WorkflowLayout>
    </PageContainer>
  )
}

function getStepDescription(step: DriverWorkflowStep) {
  switch (step) {
    case 'field-mapping':
      return 'Mappa kolumner från din fil till förarsystemets fält.'
    case 'driver-review':
      return 'Granska matchningar och bestäm åtgärder för varje rad.'
    case 'complete':
      return 'Uppdateringen är klar. Se sammanfattning och eventuella fel.'
    default:
      return ''
  }
}
